import { Box, Button, Chip, Container, Grid, Stack, Typography, useTheme, alpha } from '@mui/material';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import dashboard from '../../assets/new/dashboard.png';
import { moduleColors } from '../../theme';

const stats = [
  { value: '19,000+', label: 'Active users' },
  { value: '22,500+', label: 'Services managed' },
  { value: '7+', label: 'ISP partners' },
];

const HeroSection = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      component="section"
      id="hero"
      aria-label="hero section"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        bgcolor: 'background.default',
        pt: { xs: '120px', md: '160px' },
        pb: { xs: 8, md: 12 },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -200,
          right: -150,
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, isDark ? 0.22 : 0.16)}, transparent 65%)`,
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: -250,
          left: -150,
          width: 560,
          height: 560,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(moduleColors.connect, isDark ? 0.16 : 0.12)}, transparent 65%)`,
          pointerEvents: 'none',
        },
      }}
    >
      <Container sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} lg={6}>
            <Chip
              label="The complete solution for ISPs"
              sx={{
                mb: 3,
                px: 1,
                fontWeight: 600,
                color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            />
            <Typography variant="h1" component="h1" sx={{ mb: 3 }}>
              Run your entire ISP{' '}
              <Box
                component="span"
                sx={{
                  background: `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.primary.dark})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                from one platform
              </Box>
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4, maxWidth: 560 }}>
              Billing, customers, services, FreeRadius internet, OLT management,
              fiscalization and online card payments — modular, and built for
              Albanian ISPs. Pay only for what you use, from €50/month.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                component={Link}
                to="/pricing"
                variant="contained"
                color="primary"
                size="large"
                endIcon={<ArrowForwardIcon />}
              >
                Build your plan
              </Button>
              <Button
                component="a"
                href="https://connect.solvenda.org"
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                color="secondary"
                size="large"
                startIcon={<CreditScoreIcon />}
                sx={{ color: 'secondary.main' }}
              >
                Pay a bill
              </Button>
            </Stack>

            <Stack direction="row" spacing={4} sx={{ mt: 5, flexWrap: 'wrap', gap: 2 }}>
              {stats.map((s) => (
                <Box key={s.label}>
                  <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 800 }}>
                    {s.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {s.label}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Box
              sx={{
                position: 'relative',
                borderRadius: 4,
                p: { xs: 1, md: 1.25 },
                background: `linear-gradient(150deg, ${alpha(theme.palette.primary.main, 0.5)}, ${alpha(moduleColors.connect, 0.3)})`,
                boxShadow: '0 30px 70px rgba(0,0,0,0.45)',
              }}
            >
              <Box
                component="img"
                src={dashboard}
                alt="SOLVENDA ISP management dashboard"
                loading="eager"
                sx={{ width: '100%', height: 'auto', borderRadius: 3, display: 'block' }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default HeroSection;
